'use client'

import React from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { cn } from '@/lib/utils'
import { usePendingNav } from '@/contexts/PendingNavContext'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

interface NavLinkProps {
  href: string
  children: React.ReactNode
  icon?: React.ReactElement
  activeIcon?: React.ReactElement
  className?: string
  badge?: React.ReactNode
  /** Vertical rail (app sidebar); labels always shown when parent has room */
  variant?: 'default' | 'sidebar'
  /** Icon-only square (collapsed rail); use with variant="sidebar" */
  iconOnly?: boolean
  /** Sidebar only: render as inactive when true (e.g. no workspace) */
  disabled?: boolean
  /** Sidebar: slightly smaller row (secondary items below main nav when expanded). */
  density?: 'default' | 'compact'
  /** Force prefetch regardless of viewport visibility */
  prefetch?: boolean
  /** Optional active-path override for nav items with dynamic destination hrefs. */
  activeMatch?: (pathname: string) => boolean
  /** Sidebar expanded row: trailing control (e.g. pin button) aligned to the right */
  trailing?: React.ReactNode
}

const NavLink: React.FC<NavLinkProps> = ({
  href,
  children,
  icon,
  activeIcon,
  className,
  badge,
  variant = 'default',
  iconOnly = false,
  disabled = false,
  density = 'default',
  prefetch,
  activeMatch,
  trailing,
}) => {
  // usePathname() can briefly be null/undefined during hydration (Next 15 + parallel routes).
  const pathname = usePathname() ?? ''
  const isActive = activeMatch ? activeMatch(pathname) : pathname === href || pathname.startsWith(href + '/')
  const pendingNav = usePendingNav()
  const isPending = pendingNav?.pendingHref === href
  const setPendingHref = pendingNav?.setPendingHref
  const isActiveOrPending = isActive || isPending

  const compactSidebar = variant === 'sidebar' && density === 'compact'
  const rowH = compactSidebar ? 'h-8' : 'h-9'
  const textSize = compactSidebar ? 'text-[13px]' : 'text-[13px]'
  const iconBox = compactSidebar ? 'h-8 w-8' : 'h-9 w-9'
  const iconPx = compactSidebar ? 17 : 18
  const iconStrokeWidth = 2

  type SidebarIconProps = { className?: string; size?: number; strokeWidth?: number }

  const applySidebarIcon = (
    el: React.ReactElement<SidebarIconProps>,
    {
      active = false,
      stroke = true,
      extraClassName,
    }: { active?: boolean; stroke?: boolean; extraClassName?: string } = {}
  ) =>
    React.cloneElement(el, {
      size: iconPx,
      ...(stroke ? { strokeWidth: iconStrokeWidth } : {}),
      className: cn(
        'shrink-0',
        active ? 'text-atrium-black' : 'text-atrium-navlink-icon',
        extraClassName,
        el.props.className
      ),
    })

  const baseStyles =
    variant === 'sidebar'
      ? cn(
          'group relative flex w-full cursor-inherit items-center justify-start rounded-lg font-medium transition-[padding,gap,background-color,color] duration-200 ease-out motion-reduce:transition-none',
          rowH,
          textSize,
          disabled &&
            'cursor-not-allowed opacity-50 pointer-events-none hover:bg-transparent active:bg-transparent',
          !disabled &&
            isActive &&
            'bg-atrium-navlink-active text-atrium-black',
          !disabled &&
            !isActive &&
            'bg-transparent text-atrium-navlink-text hover:bg-atrium-navlink-active active:text-atrium-black focus-visible:text-atrium-black',
          !disabled && isActiveOrPending && !isActive && 'bg-atrium-navlink-active text-atrium-black'
        )
      : cn(
          'group relative px-3 text-sm font-normal transition-colors flex justify-center items-center gap-1.5 h-8 rounded-lg overflow-hidden',
          isActive &&
            'bg-atrium-navlink-active text-atrium-black',
          !isActive &&
            'bg-transparent text-atrium-black active:text-atrium-black focus-visible:text-atrium-black',
          isActiveOrPending && !isActive && 'bg-atrium-navlink-active'
        )

  const labelText =
    variant === 'sidebar' && typeof children === 'string' ? children : undefined

  const inner = (
    <>
      <div className={cn(iconBox, 'flex items-center justify-center shrink-0 pl-[3px]')}>
        {isActiveOrPending ? (
          activeIcon ? (
            applySidebarIcon(activeIcon, { active: true, stroke: false })
          ) : icon ? (
            applySidebarIcon(icon, { active: true })
          ) : null
        ) : activeIcon && icon ? (
          <span
            className="relative inline-flex shrink-0 items-center justify-center"
            style={{ width: iconPx, height: iconPx }}
          >
            {applySidebarIcon(icon, { extraClassName: 'transition-opacity group-active:opacity-0' })}
            {React.cloneElement(activeIcon, {
              size: iconPx,
              className: cn(
                'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 shrink-0 opacity-0 transition-opacity group-active:opacity-100',
                activeIcon.props.className
              ),
            })}
          </span>
        ) : icon ? (
          applySidebarIcon(icon)
        ) : null}
      </div>
      {variant === 'sidebar' ? (
        <span
          aria-hidden={iconOnly ? true : undefined}
          className={cn(
            'flex min-w-0 flex-col justify-center overflow-hidden transition-[max-width,opacity,transform] duration-200 ease-out motion-reduce:transition-none',
            iconOnly
              ? 'pointer-events-none max-w-0 translate-y-1 opacity-0'
              : cn(
                  'max-w-[min(256px,calc(100%-1.5rem))] translate-y-0 opacity-100',
                  trailing && 'min-w-0 flex-1'
                )
          )}
        >
          <span className="truncate">{children}</span>
        </span>
      ) : (
        <span className="hidden lg:inline">{children}</span>
      )}
      {badge && (
        <span
          className={cn(
            variant === 'sidebar'
              ? cn(
                  'shrink-0 overflow-hidden transition-[max-width,opacity,transform] duration-200 ease-out motion-reduce:transition-none',
                  iconOnly
                    ? 'pointer-events-none max-w-0 translate-y-1 opacity-0'
                    : 'max-w-[min(256px,calc(100%-1.5rem))] translate-y-0 opacity-100'
                )
              : 'hidden lg:inline'
          )}
        >
          {badge}
        </span>
      )}
      {/* <span
        aria-hidden
        className={cn(
          'absolute bottom-0 left-0 right-0 h-[2px] transition-transform duration-300 ease-out group-active:bg-accent-600',
          isActiveOrPending ? 'translate-y-0 bg-accent-600' : 'translate-y-full bg-atrium-border group-hover:translate-y-0'
        )}
      /> */}
    </>
  )

  const shellClass = cn(baseStyles, className)
  const hasTrailingShell = variant === 'sidebar' && trailing && !iconOnly

  const link =
    disabled && variant === 'sidebar' ? (
      <span className={shellClass} aria-disabled="true" aria-label={iconOnly && labelText ? labelText : undefined}>
        {inner}
      </span>
    ) : hasTrailingShell ? (
      <div className={shellClass}>
        <Link
          href={href}
          onClick={() => setPendingHref?.(href)}
          className="flex min-w-0 flex-1 items-center justify-start self-stretch outline-none"
          prefetch={prefetch}
        >
          {inner}
        </Link>
        <span className="ml-auto shrink-0 pr-0.5">{trailing}</span>
      </div>
    ) : (
      <Link
        href={href}
        onClick={() => setPendingHref?.(href)}
        className={shellClass}
        prefetch={prefetch}
        aria-label={variant === 'sidebar' && iconOnly && labelText ? labelText : undefined}
      >
        {inner}
      </Link>
    )

  if (variant === 'sidebar' && iconOnly && labelText) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right">
          <span className="font-normal">{labelText}</span>
        </TooltipContent>
      </Tooltip>
    )
  }

  return link
}

export default NavLink
